import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Play, Plus } from "lucide-react";
import { from, issuerLabel, postgrestSource, rpc, unwrap, useAction, useHasRole, useIssuers, useOrg, type PersonLite } from "@vixio/data";
import { PersonPicker } from "@vixio/core-ui";
import {
  Button, Checkbox, ConfirmDialog, DataTable, DateInput, Dialog, DocInput, KpiRow, KpiTile, Menu, MoneyInput, NumberInput, PageHeader, Select,
  StatusFromMap, Tabs, TextInput, formatDate, formatInt, formatMoney, todayIso,
} from "@vixio/ui";
import { CHARGE_KINDS, INTERVALS, PROVIDERS, SUB_STATUS, type ChargeKind, type Interval, type Plan, type Provider, type Subscription } from "./api";

export function useMrr(orgId: string) {
  return useQuery({
    queryKey: ["billing", "v_mrr", orgId],
    queryFn: async () => {
      const rows = await unwrap<Array<{ mrr_cents: number; active_subscriptions: number }>>(from("billing", "v_mrr").select("mrr_cents, active_subscriptions").eq("org_id", orgId));
      return { mrr: rows.reduce((a, r) => a + Number(r.mrr_cents), 0), active: rows.reduce((a, r) => a + Number(r.active_subscriptions), 0) };
    },
  });
}

export function SubscriptionsPage() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const mrr = useMrr(org.id);
  const [creating, setCreating] = useState(false);
  const [changing, setChanging] = useState<{ sub: Subscription; to: "active" | "paused" | "canceled" } | null>(null);
  const [cycle, setCycle] = useState(false);

  const source = useMemo(
    () => postgrestSource<Subscription>({ schema: "billing", table: "subscriptions", orgId: org.id, searchColumns: ["counterparty_name", "description"], tiebreak: "id" }),
    [org.id],
  );
  const setStatus = useAction(
    (a: { id: string; to: "active" | "paused" | "canceled"; reason: string }) => rpc("billing", "set_subscription_status", { p_subscription: a.id, p_status: a.to, p_reason: a.reason || null }),
    { invalidate: [["billing"]], success: "Assinatura atualizada" },
  );
  const runCycle = useAction(() => rpc("billing", "run_cycle", { p_date: null, p_org: org.id }), {
    invalidate: [["billing"], ["finance"]],
    success: (r) => `Ciclo rodado: ${r.billed} período(s) faturado(s), ${r.ended} encerrada(s)${r.errors.length ? `, ${r.errors.length} erro(s)` : ""}`,
  });

  return (
    <div className="vx-content">
      <PageHeader
        title="Assinaturas"
        description="Retainer, mentoria e SaaS. O ciclo diário fatura cada período uma única vez: cria o título no financeiro e, com provedor, a cobrança."
        actions={
          <>
            {isAdmin ? (
              <Button icon={<Play />} onClick={() => setCycle(true)}>
                Rodar ciclo agora
              </Button>
            ) : null}
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Nova assinatura
            </Button>
          </>
        }
      />
      <KpiRow>
        <KpiTile label="Receita recorrente mensal (MRR)" value={formatMoney(mrr.data?.mrr ?? 0)} foot="assinaturas ativas normalizadas para o mês" />
        <KpiTile label="Assinaturas ativas" value={formatInt(mrr.data?.active ?? 0)} />
        <KpiTile label="Receita anualizada" value={formatMoney((mrr.data?.mrr ?? 0) * 12)} />
      </KpiRow>
      <div className="vx-section">
        <Tabs
          label="Assinaturas e planos"
          tabs={[
            {
              value: "assinaturas",
              label: "Assinaturas",
              content: (
                <DataTable<Subscription>
                  caption="Assinaturas"
                  source={source}
                  rowKey={(s) => s.id}
                  initialSort={{ key: "next_billing_on", dir: "asc" }}
                  initialFilters={{ status: "active" }}
                  searchPlaceholder="Buscar por pagador ou descrição"
                  exportName="assinaturas"
                  filters={[{ key: "status", label: "Situação", all: "Todas", options: Object.entries(SUB_STATUS).map(([value, s]) => ({ value, label: s.label })) }]}
                  columns={[
                    { key: "counterparty_name", header: "Pagador", sortable: true, cell: (s) => <span className="vx-table__primary">{s.counterparty_name}<span className="vx-table__sub">{s.description}</span></span> },
                    { key: "amount_cents", header: "Valor", kind: "money", sortable: true },
                    { key: "billing_interval", header: "Período", cell: (s) => INTERVALS[s.billing_interval], csv: (s) => INTERVALS[s.billing_interval] },
                    { key: "starts_on", header: "Início", kind: "date", sortable: true },
                    { key: "ends_on", header: "Fim", cell: (s) => (s.ends_on ? formatDate(s.ends_on) : <span className="muted">sem prazo</span>), csv: (s) => formatDate(s.ends_on) },
                    { key: "next_billing_on", header: "Próximo faturamento", kind: "date", sortable: true },
                    { key: "charge", header: "Cobrança", cell: (s) => (s.charge_provider ? `${PROVIDERS[s.charge_provider]} · ${CHARGE_KINDS[s.charge_kind!]}` : <span className="muted">manual</span>), csv: (s) => (s.charge_provider ? `${s.charge_provider}/${s.charge_kind}` : "manual") },
                    { key: "status", header: "Situação", cell: (s) => <StatusFromMap map={SUB_STATUS} value={s.status} />, csv: (s) => SUB_STATUS[s.status]?.label ?? s.status },
                    {
                      key: "actions",
                      header: <span className="sr-only">Ações</span>,
                      align: "right",
                      csv: false,
                      cell: (s) =>
                        s.status === "active" || s.status === "paused" ? (
                          <Menu
                            trigger={<Button size="sm">Ações</Button>}
                            items={[
                              s.status === "active"
                                ? { label: "Pausar", onSelect: () => setChanging({ sub: s, to: "paused" }) }
                                : { label: "Retomar", onSelect: () => setStatus.mutate({ id: s.id, to: "active", reason: "" }) },
                              { label: "Cancelar", danger: true, disabled: !isAdmin, onSelect: () => setChanging({ sub: s, to: "canceled" }) },
                            ]}
                          />
                        ) : null,
                    },
                  ]}
                />
              ),
            },
            { value: "planos", label: "Planos", content: <PlansTab /> },
          ]}
        />
      </div>
      <NewSubscriptionDialog open={creating} onOpenChange={setCreating} />
      <ConfirmDialog
        open={changing !== null}
        onOpenChange={(o) => !o && setChanging(null)}
        title={changing?.to === "canceled" ? "Cancelar assinatura" : "Pausar assinatura"}
        confirmLabel={changing?.to === "canceled" ? "Cancelar assinatura" : "Pausar"}
        destructive={changing?.to === "canceled"}
        confirmText={changing?.to === "canceled" ? "CANCELAR" : undefined}
        reason={{ label: "Motivo", min: 3, max: 500 }}
        onConfirm={(reason) => setStatus.mutateAsync({ id: changing!.sub.id, to: changing!.to, reason })}
      >
        <p>
          {changing?.sub.counterparty_name}: {changing?.to === "paused" ? "os períodos pausados não são faturados ao retomar." : "não haverá novos faturamentos. Títulos já criados continuam no financeiro."}
        </p>
      </ConfirmDialog>
      <ConfirmDialog open={cycle} onOpenChange={setCycle} title="Rodar ciclo de faturamento" confirmLabel="Rodar agora" onConfirm={() => runCycle.mutateAsync(undefined)}>
        <p>Fatura os períodos vencidos até hoje ({formatDate(todayIso())}) das assinaturas ativas desta organização. Rodar de novo não fatura duas vezes.</p>
      </ConfirmDialog>
    </div>
  );
}

function PlansTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const [editing, setEditing] = useState<Plan | "new" | null>(null);
  const plans = useQuery({ queryKey: ["billing", "plans", org.id], queryFn: () => unwrap<Plan[]>(from("billing", "plans").select("*").eq("org_id", org.id).order("name")) });
  return (
    <>
      <DataTable<Plan>
        caption="Planos"
        data={plans.data ?? []}
        loading={plans.isLoading}
        rowKey={(p) => p.id}
        onRowClick={isAdmin ? setEditing : undefined}
        toolbar={isAdmin ? <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing("new")}>Novo plano</Button> : null}
        empty="Nenhum plano. Assinaturas também podem ter valor próprio, sem plano."
        columns={[
          { key: "name", header: "Plano", cell: (p) => <span className="vx-table__primary">{p.name}</span> },
          { key: "amount_cents", header: "Valor", kind: "money" },
          { key: "billing_interval", header: "Período", cell: (p) => INTERVALS[p.billing_interval] },
          { key: "offer_slug", header: "Oferta", cell: (p) => <span className="mono">{p.offer_slug ?? "-"}</span> },
          { key: "active", header: "Situação", cell: (p) => (p.active ? "ativo" : <span className="muted">inativo</span>) },
        ]}
      />
      <PlanDialog plan={editing === "new" ? null : editing} open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} />
    </>
  );
}

function PlanDialog({ plan, open, onOpenChange }: { plan: Plan | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const [f, setF] = useState({ name: "", amount: null as number | null, interval: "monthly" as Interval, offer: "", description: "", active: true });
  useEffect(() => {
    if (open) setF({ name: plan?.name ?? "", amount: plan?.amount_cents ?? null, interval: plan?.billing_interval ?? "monthly", offer: plan?.offer_slug ?? "", description: plan?.description ?? "", active: plan?.active ?? true });
  }, [open, plan]);
  const save = useAction(
    () => rpc("billing", "upsert_plan", { p_org: org.id, p_name: f.name.trim(), p_amount_cents: f.amount ?? 0, p_interval: f.interval, p_offer_slug: f.offer || null, p_description: f.description || null, p_active: f.active, p_id: plan?.id ?? null }),
    { invalidate: [["billing", "plans"]], success: "Plano salvo", onSuccess: () => onOpenChange(false) },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={plan ? `Editar ${plan.name}` : "Novo plano"}
      onSubmit={() => f.name.trim() && f.amount && save.mutate(undefined)}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="vx-form-grid">
        <TextInput label="Nome" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
        <MoneyInput label="Valor por período" value={f.amount} onChange={(c) => setF({ ...f, amount: c })} />
        <Select label="Período" value={f.interval} onChange={(v) => setF({ ...f, interval: v as Interval })} options={Object.entries(INTERVALS).map(([value, label]) => ({ value, label }))} />
        <TextInput label="Oferta" value={f.offer} onChange={(v) => setF({ ...f, offer: v })} className="mono" hint="Identificador da oferta (categoria e CNPJ)." />
        <TextInput className="span-2" label="Descrição" value={f.description} onChange={(v) => setF({ ...f, description: v })} />
        <div className="span-2">
          <Checkbox label="Ativo" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
        </div>
      </div>
    </Dialog>
  );
}

function NewSubscriptionDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const issuers = useIssuers(org.id);
  const plans = useQuery({ queryKey: ["billing", "plans", org.id], queryFn: () => unwrap<Plan[]>(from("billing", "plans").select("*").eq("org_id", org.id).eq("active", true).order("name")) });
  const [person, setPerson] = useState<PersonLite | null>(null);
  const [f, setF] = useState({ name: "", doc: "", docOk: true, description: "", plan: "", amount: null as number | null, interval: "monthly" as Interval, starts: todayIso(), term: null as number | null, dueDays: 7 as number | null, issuer: "", provider: "", kind: "pix" as ChargeKind });
  useEffect(() => {
    if (person && !f.name) setF((x) => ({ ...x, name: person.company_name || person.full_name }));
  }, [person, f.name]);
  const create = useAction(
    () =>
      rpc("billing", "create_subscription", {
        p_org: org.id,
        p_counterparty_name: f.name.trim(),
        p_description: f.description.trim(),
        p_starts_on: f.starts,
        p_plan: f.plan || null,
        p_amount_cents: f.plan ? null : f.amount,
        p_interval: f.plan ? null : f.interval,
        p_issuer: f.issuer || null,
        p_person: person?.id ?? null,
        p_counterparty_doc: f.doc || null,
        p_term_months: f.term,
        p_due_days: f.dueDays ?? 7,
        p_charge_provider: (f.provider || null) as Provider | null,
        p_charge_kind: f.provider ? f.kind : null,
      }),
    { invalidate: [["billing"]], success: "Assinatura criada", onSuccess: () => onOpenChange(false) },
  );
  const ok = f.name.trim().length >= 2 && f.description.trim() && (f.plan || (f.amount && f.amount > 0)) && f.docOk;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Nova assinatura"
      description="Mensal aceita pelo link da proposta vira assinatura sozinha. Use aqui para contratos fechados fora do sistema."
      onSubmit={() => ok && create.mutate(undefined)}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!ok}>
            Criar assinatura
          </Button>
        </>
      }
    >
      <div className="vx-form-grid">
        <PersonPicker label="Pessoa (opcional)" value={person} onChange={setPerson} />
        <TextInput label="Pagador" value={f.name} onChange={(v) => setF({ ...f, name: v })} required />
        <DocInput label="CPF ou CNPJ" value={f.doc} onChange={(v, valid) => setF({ ...f, doc: v, docOk: v === "" || valid })} />
        <TextInput label="Descrição" value={f.description} onChange={(v) => setF({ ...f, description: v })} required hint="Ex.: Consultoria estratégica (retainer)" />
        <Select label="Plano" value={f.plan} onChange={(v) => setF({ ...f, plan: v })} placeholder="Valor próprio" options={(plans.data ?? []).map((p) => ({ value: p.id, label: `${p.name} (${formatMoney(p.amount_cents)})` }))} />
        {!f.plan ? (
          <>
            <MoneyInput label="Valor por período" value={f.amount} onChange={(c) => setF({ ...f, amount: c })} />
            <Select label="Período" value={f.interval} onChange={(v) => setF({ ...f, interval: v as Interval })} options={Object.entries(INTERVALS).map(([value, label]) => ({ value, label }))} />
          </>
        ) : null}
        <DateInput label="Início" value={f.starts} onChange={(v) => setF({ ...f, starts: v })} />
        <NumberInput label="Prazo" suffix="meses" value={f.term} onChange={(v) => setF({ ...f, term: v })} min={1} max={120} hint="Vazio: sem prazo." />
        <NumberInput label="Vencimento" suffix="dias após o início do período" value={f.dueDays} onChange={(v) => setF({ ...f, dueDays: v })} min={0} max={60} />
        <Select label="CNPJ emissor" value={f.issuer} onChange={(v) => setF({ ...f, issuer: v })} placeholder="Padrão pela oferta" options={(issuers.data ?? []).map((i) => ({ value: i.id, label: issuerLabel(i) }))} />
        <Select label="Cobrança automática" value={f.provider} onChange={(v) => setF({ ...f, provider: v })} placeholder="Sem cobrança automática" options={Object.entries(PROVIDERS).map(([value, label]) => ({ value, label }))} />
        {f.provider ? <Select label="Meio" value={f.kind} onChange={(v) => setF({ ...f, kind: v as ChargeKind })} options={Object.entries(CHARGE_KINDS).map(([value, label]) => ({ value, label }))} /> : null}
      </div>
    </Dialog>
  );
}
